<template>
    <modal
        name="hire-enquiry-modal"
        width="50%"
        height="auto"
        scrollable
        @before-open="handleModalBeforeOpen"
    >
        <div class="py-8 px-10">

            <div class="flex justify-between">
                <div class="text-xl font-semibold tracking-wide">
                    Hire enquiry for {{ product.name }}
                </div>
                <CloseIcon
                    class="close-icon mt-1"
                    @click="closeModal"
                />
            </div>

            <div class="mt-4">
                <div class="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <Input
                        v-model="firstName"
                        label="First name"
                        type="text"
                        :invalid="$v.firstName.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.firstName.required && $v.firstName.$dirty">
                                Please enter your first name.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="lastName"
                        label="Last name"
                        type="text"
                        :invalid="$v.lastName.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.lastName.required && $v.lastName.$dirty">
                                Please enter your last name.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="email"
                        label="Email"
                        type="email"
                        :invalid="$v.email.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.email.required && $v.email.$dirty">
                                Please enter your email address.
                            </div>
                            <div v-if="!$v.email.email && $v.email.$dirty">
                                Please enter a valid email address.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="phone"
                        label="Phone"
                        type="tel"
                        :invalid="$v.phone.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.phone.required && $v.phone.$dirty">
                                Please enter your phone number.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="dates"
                        label="Event date"
                        type="text"
                        :invalid="$v.dates.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.dates.required && $v.dates.$dirty">
                                Please enter your event date.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="eventStartTime"
                        label="Event start time"
                        type="text"
                    />

                    <Input
                        v-model="venue"
                        label="Venue"
                        type="text"
                    />
                </div>

            </div>

            <div class="flex items-center mt-6">
                <Button
                    :loading="loading"
                    text="Send enquiry"
                    class="mr-8"
                    @click="send"
                >
                    <template #icon>
                        <EnvelopeIcon class="h-5 w-auto mr-2" />
                    </template>
                </Button>
                <span
                    class="cancel"
                    @click="closeModal"
                >
                    Cancel
                </span>
            </div>

        </div>
    </modal>
</template>

<script>
import EnvelopeIcon from 'static/fonts/LineIcons/envelope.svg';
import { email, required } from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import CloseIcon from '~/static/fonts/LineIcons/close.svg';
import Button from '~/components/Button';
import Input from '@/components/Input.vue';
import handleException from '@/plugins/error-handler.client';

export default {

    components: {
        Input,
        EnvelopeIcon,
        CloseIcon,
        Button,
    },

    props: {
        product: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dates: '',
            eventStartTime: '',
            venue: '',
            loading: false,
        };
    },

    methods: {
        handleModalBeforeOpen() {
            if (this.$store.getters['user/isLoggedIn']) {
                this.firstName = this.$store.getters['user/firstName'];
                this.lastName = this.$store.getters['user/lastName'];
                this.email = this.$store.getters['user/email'];
                this.phone = this.$store.getters['user/phone'];
            }
        },
        async send() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            this.loading = true;
            try {
                await this.$axios.$post('/hire-enquiry', {
                    productId: this.product.id,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    phone: this.phone,
                    dates: this.dates,
                    eventStartTime: this.eventStartTime,
                    venue: this.venue,
                });

                await Swal.fire({
                    icon: 'success',
                    title: 'Enquiry sent',
                    text: 'Our team will check the availability of this product and get back to you shortly.',
                });

                this.$modal.hide('hire-enquiry-modal');
            } catch (e) {
                handleException(e);
            } finally {
                this.loading = false;
            }
        },
        closeModal() {
            this.$modal.hide('hire-enquiry-modal');
        },
    },

    validations: {
        firstName: {
            required,
        },
        lastName: {
            required,
        },
        email: {
            required,
            email,
        },
        phone: {
            required,
        },
        dates: {
            required,
        },
    },

};
</script>

<style lang="scss" scoped>
.close-icon {
    @apply h-5 cursor-pointer transition-fast;
    &:hover {
        @apply opacity-50;
    }
}
.cancel {
    @apply font-semibold text-grey transition-fast cursor-pointer;
    &:hover {
        @apply text-primary;
    }
}
</style>
